import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, Space, Table, Tag, message } from 'antd';
import dayjs from 'dayjs';
import * as FileSaver from 'file-saver';
import XLSX from 'sheetjs-style';
import { fetchReportSkus, fetchReportSkusByIds, useReportSkus, useReportSkusByIds } from '../../hooks/useReports';

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';
const EXPORT_CAP = 5000;
const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';

function formatDate(value) {
    return value ? dayjs(value).format(DATE_FORMAT) : '—';
}

function toExcelRow(row) {
    return {
        SKU: row.skuName,
        Barcodes: (row.barcodes || []).join(', '),
        Order: row.orderName || '',
        Inbound: formatDate(row.inboundTimestamp),
        'DD approved': row.isDDApproved ? 'Y' : 'N',
        'DD approved at': formatDate(row.ddApprovedTimestamp),
        'Shoot complete': formatDate(row.shootCompletionTime),
        Packed: formatDate(row.packedAt),
        Outbound: formatDate(row.outboundTimestamp),
        Delivered: formatDate(row.firstDeliveryTimestamp),
        'Image delivered': formatDate(row.firstDeliveryImage),
        'Content delivered': formatDate(row.firstDeliveryContent),
        Support: row.markedAsSupport ? 'Y' : 'N',
        Damaged: row.isDamaged ? 'Y' : 'N',
        Shootable: row.isShootable ? 'Y' : 'N',
        Reordered: row.isReorder ? 'Y' : 'N',
    };
}

function downloadExcel(rows, fileName) {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = { Sheets: { data: ws }, SheetNames: ['data'] };
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([excelBuffer], { type: EXCEL_TYPE }), `${fileName}.xlsx`);
}

const columns = [
    {
        title: 'SKU',
        dataIndex: 'skuName',
        width: 220,
        ellipsis: true,
        fixed: 'left',
        render: (value, row) => value || row.skuId || '—',
    },
    {
        title: 'Barcodes',
        dataIndex: 'barcodes',
        width: 160,
        ellipsis: true,
        render: barcodes => (barcodes || []).join(', ') || '—',
    },
    { title: 'Order', dataIndex: 'orderName', width: 140, render: value => value || '—' },
    { title: 'Inbound', dataIndex: 'inboundTimestamp', width: 150, render: formatDate },
    { title: 'DD approved at', dataIndex: 'ddApprovedTimestamp', width: 150, render: formatDate },
    { title: 'Shoot complete', dataIndex: 'shootCompletionTime', width: 150, render: formatDate },
    { title: 'Packed', dataIndex: 'packedAt', width: 130, render: formatDate },
    { title: 'Delivered', dataIndex: 'firstDeliveryTimestamp', width: 150, render: formatDate },
    { title: 'Image', dataIndex: 'firstDeliveryImage', width: 150, render: formatDate },
    { title: 'Content', dataIndex: 'firstDeliveryContent', width: 150, render: formatDate },
    {
        title: 'Flags',
        width: 220,
        render: (_, row) => (
            <Space size={4} wrap>
                {row.isDDApproved ? <Tag>DD</Tag> : null}
                {row.isRawQCComplete ? <Tag color="green">Raw QC</Tag> : null}
                {row.isOutbound ? <Tag color="blue">Outbound</Tag> : null}
                {row.markedAsSupport ? <Tag>Support</Tag> : null}
                {row.isDamaged ? <Tag color="red">Damaged</Tag> : null}
                {row.isReorder ? <Tag color="orange">Reorder</Tag> : null}
            </Space>
        ),
    },
];

export default function DrilldownModal({ open, title, filters, bucket, skuIds, onClose }) {
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [exporting, setExporting] = useState(false);
    const isStage = Array.isArray(skuIds);

    useEffect(() => {
        setPageNum(1);
        setExporting(false);
    }, [open, bucket, skuIds]);

    const bucketParams = useMemo(
        () => (open && filters && bucket && !isStage ? { ...filters, bucket, pageNum, pageSize } : null),
        [open, filters, bucket, isStage, pageNum, pageSize]
    );
    const idsBody = useMemo(
        () => (open && filters && isStage ? { ...filters, skuIds, pageNum, pageSize } : null),
        [open, filters, isStage, skuIds, pageNum, pageSize]
    );
    const bucketQuery = useReportSkus(bucketParams, !!bucketParams);
    const idsQuery = useReportSkusByIds(idsBody, !!(idsBody && skuIds?.length));
    const { data, isFetching } = isStage ? idsQuery : bucketQuery;
    const payload = data?.data?.data;
    const skus = isStage && !skuIds?.length ? [] : payload?.skus || [];
    const totalCount = isStage && !skuIds?.length ? 0 : payload?.totalCount || 0;

    const onExport = async () => {
        setExporting(true);
        try {
            const response = isStage
                ? await fetchReportSkusByIds({
                    ...filters,
                    skuIds,
                    pageNum: 1,
                    pageSize: Math.min(Math.max(totalCount, 1), EXPORT_CAP),
                })
                : await fetchReportSkus({
                    ...filters,
                    bucket,
                    pageNum: 1,
                    pageSize: Math.min(Math.max(totalCount, 1), EXPORT_CAP),
                });
            const rows = (response?.data?.data?.skus || []).map(toExcelRow);
            if (!rows.length) {
                message.warning('No SKUs to export');
                return;
            }
            if (totalCount > EXPORT_CAP) {
                message.info(`Export capped at ${EXPORT_CAP} of ${totalCount} SKUs`);
            }
            downloadExcel(rows, `${filters?.companyCode || 'report'}-${bucket || 'stage'}`);
        } catch (err) {
            const payloadMessage = err?.response?.data?.message;
            message.error(typeof payloadMessage === 'string' ? payloadMessage : 'Could not export SKUs');
        } finally {
            setExporting(false);
        }
    };

    return (
        <Modal
            title={title}
            open={open}
            onCancel={onClose}
            footer={null}
            width="90vw"
            wrapClassName="reports-drilldown-modal"
            destroyOnClose
        >
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <Button type="primary" onClick={onExport} loading={exporting}>
                    Export Excel
                </Button>
            </div>
            <Table
                rowKey="skuId"
                loading={isFetching}
                dataSource={skus}
                columns={columns}
                scroll={{ x: 1830 }}
                pagination={{
                    current: pageNum,
                    pageSize,
                    total: totalCount,
                    showSizeChanger: true,
                    showTotal: count => `${count.toLocaleString()} SKUs`,
                    pageSizeOptions: [50, 100, 200],
                    onChange: (page, size) => {
                        setPageNum(page);
                        setPageSize(size);
                    },
                }}
            />
        </Modal>
    );
}
